import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Grid, IconButton, Stack, Tooltip } from '@mui/material';

import Circular from 'components/common/CustomCircular';

import DetailPanel from './DetailPanel';
import ListPanel, { ListPanelItem } from './ListPanel';
import SearchFilter, { Condition, Filter } from './SearchFilter';

type ConditionListProps = {
  /** 検索条件定義 */
  conditions: Condition[];
  /** リスト表示用アイテム */
  items: ListPanelItem[];
  /** 選択中アイテムのID */
  selectedId?: string;
  /** アイテム選択ハンドラ */
  onSelect: (item: ListPanelItem) => void;
  /** 検索実行ハンドラ */
  onSearch: (filters: Filter[]) => void;
  /** 新規作成ボタンクリックハンドラ */
  onCreateClick?: () => void;
  /** 新規作成ボタンのラベル */
  createButtonLabel?: string;
  /** 新規作成ボタンを表示するか */
  showCreateButton?: boolean;
  /** リストアイテムのカスタムレンダリング */
  renderItem?: (item: ListPanelItem, selected: boolean) => React.ReactNode;
  /** 詳細パネルのコンテンツ */
  children: React.ReactNode;
  /** ローディング表示 */
  isProgress?: boolean;
  /** リストの高さ */
  listHeight?: string;
  /** 詳細パネルの高さ */
  detailHeight?: string;
  /** リストパネルを折りたたみ可能にするか */
  collapsible?: boolean;
  /** リストパネルの初期折りたたみ状態 */
  defaultCollapsed?: boolean;
  /** 詳細パネルのフッター（スクロール領域外に固定表示） */
  detailFooter?: React.ReactNode;
  /** 検索条件の初期値 */
  initialFilters?: Filter[];
  /** 検索条件保存機能用の条件キー（画面ごとに固定値を設定） */
  conditionKey?: string;
  /**
   * user_profile(AUTO) 用の profile_key（例: "member-partner-list-search"）。
   * 指定時、初期表示で「デフォルト★の次段」として最後の検索条件を復元し、検索実行のたびに保存する。
   */
  userProfileKey?: string;
  /** 新規作成ボタンの横に追加表示するアクション要素 */
  extraHeaderActions?: React.ReactNode;
  /** リストが0件のときに表示する空状態（任意・未指定時は既定の空状態） */
  listEmptyState?: React.ReactNode;
  /** 詳細パネルに表示する内容が無いときの空状態（任意） */
  detailEmptyState?: React.ReactNode;
  /** 詳細パネルを空状態にするか（detailEmptyState と併用） */
  detailIsEmpty?: boolean;
};

const ConditionList: React.FC<ConditionListProps> = ({
  conditions,
  items,
  selectedId,
  onSelect,
  onSearch,
  onCreateClick,
  createButtonLabel = '新規作成',
  showCreateButton = true,
  renderItem,
  children,
  isProgress = false,
  listHeight,
  detailHeight,
  collapsible = false,
  defaultCollapsed = false,
  detailFooter,
  initialFilters = [],
  conditionKey,
  userProfileKey,
  extraHeaderActions,
  listEmptyState,
  detailEmptyState,
  detailIsEmpty = false,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const isCollapsed = collapsible && collapsed;

  // 初回ロード（＝まだ1件も無い状態でのロード中）はリストのスケルトンで表現し、
  // 全画面オーバーレイのスピナーは出さない（二重ローディング回避）。
  // 既にデータがある状態での再取得・更新中はオーバーレイのスピナーを使う。
  const showSkeleton = isProgress && items.length === 0;
  const showOverlay = isProgress && items.length > 0;

  const createAction =
    showCreateButton && onCreateClick ? (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title={createButtonLabel}>
          <IconButton
            size="small"
            onClick={onCreateClick}
            sx={{
              '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <AddIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        {extraHeaderActions}
      </Stack>
    ) : extraHeaderActions ? (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {extraHeaderActions}
      </Stack>
    ) : undefined;

  return (
    <>
      <Grid container spacing={1.5}>
        <Grid size={12}>
          <Stack direction="row" spacing={1} alignItems="center">
            {conditionKey && (
              <Tooltip title="条件詳細">
                <IconButton
                  size="small"
                  onClick={() => setDetailDialogOpen(true)}
                  sx={{
                    flexShrink: 0,
                    '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                  }}
                >
                  <TuneIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ flex: 1 }}>
              <SearchFilter
                filters={initialFilters}
                conditions={conditions}
                orders={[]}
                onFiltersChange={onSearch}
                conditionKey={conditionKey}
                userProfileKey={userProfileKey}
                detailDialogOpen={detailDialogOpen}
                onDetailDialogClose={() => setDetailDialogOpen(false)}
              />
            </Box>
          </Stack>
        </Grid>
        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* リストパネル */}
            {!isCollapsed && (
              <Box
                sx={{
                  width: '40%',
                  flexShrink: 0,
                  position: 'relative',
                  transition: 'width 0.25s ease',
                }}
              >
                <ListPanel
                  items={items}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  renderItem={renderItem}
                  height={listHeight}
                  headerAction={createAction}
                  loading={showSkeleton}
                  emptyState={listEmptyState}
                />
                {/* ← トグル: リストパネル内の右中央にフロート */}
                {collapsible && (
                  <Tooltip title="リストを非表示" placement="left">
                    <IconButton
                      size="small"
                      onClick={() => setCollapsed(true)}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 28,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        opacity: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          opacity: 1,
                          color: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ChevronLeftIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}

            {/* 詳細パネル */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <DetailPanel
                height={detailHeight}
                footer={detailFooter}
                emptyState={detailEmptyState}
                isEmpty={detailIsEmpty}
                collapseToggle={
                  collapsible && isCollapsed ? (
                    <Tooltip title="リストを表示" placement="right">
                      <IconButton
                        size="small"
                        onClick={() => setCollapsed(false)}
                        sx={{
                          position: 'absolute',
                          left: 4,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 28,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          opacity: 0.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            opacity: 1,
                            color: 'primary.main',
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  ) : undefined
                }
              >
                {children}
              </DetailPanel>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Circular open={showOverlay} />
    </>
  );
};

export default ConditionList;
